import React from 'react';
import { to } from 'acl';
import { Badge, Button, Modal } from '@odin-labs/components';
import {
  useGetSiteZonesModeQuery,
  useChangeZonesModeMutation,
  ZonesMode,
  ZonesModeChangeStatus,
  ZonesReadinessCode,
} from 'apollo/generated/client-operations';
import { AuthContext } from 'auth/authContext';
import { Section } from 'components/section';
import { LoadingError } from 'components/loadingError';
import { ActionFeedback } from './AccessGroupStatus';
import { modeDescriptions, modeLabels } from './presentation';
import { ZonesModeProgress } from './ZonesModeProgress';

/** Readiness gates entering Shadow from Off, or Enforce; never a way back. */
function requiresReadiness(fromMode: ZonesMode, toMode: ZonesMode): boolean {
  return (
    toMode === ZonesMode.Enforce ||
    (toMode === ZonesMode.Shadow && fromMode === ZonesMode.Off)
  );
}

function confirmationCopy(
  fromMode: ZonesMode,
  toMode: ZonesMode,
): {
  title: string;
  actionText: string;
  description: string;
} {
  switch (toMode) {
    case ZonesMode.Enforce:
      return {
        title: 'Enable Enforce?',
        actionText: 'Confirm Enforce',
        description:
          'Only workers with current site access and an active group granting a zone will be admitted to that zone. Workers without a qualifying group will lose access. A revoked default membership stays revoked; another active group can still grant access. Provider access changes during synchronization; the mode remains Shadow until verification succeeds.',
      };
    case ZonesMode.Shadow:
      if (fromMode === ZonesMode.Enforce)
        return {
          title: 'Return to Shadow?',
          actionText: 'Confirm return to Shadow',
          description:
            'Every worker with current site access will regain access to all published zones, regardless of group membership. Bans and suspensions still apply. Membership history is kept. Provider access changes during synchronization; the mode remains Enforce until verification succeeds.',
        };
      return {
        title: 'Enable Shadow for this site?',
        actionText: 'Confirm enable',
        description:
          'Odin will enroll workers, check current access and connect the default group. Site access stays unchanged. The mode remains Off until every step succeeds.',
      };
    case ZonesMode.Off:
      return {
        title: 'Roll back this site to Off?',
        actionText: 'Confirm rollback',
        description:
          'Odin will restore the existing site access path. Groups and membership history are kept, and the mode remains Shadow until rollback succeeds.',
      };
    default: {
      const unexpected: never = toMode;
      throw new Error(`Unsupported zones mode: ${unexpected}`);
    }
  }
}

export function SiteZonesMode({
  siteId,
  stepDownOnly = false,
}: {
  siteId: string;
  // Without the site's flag, offer only the way back towards Off. A site
  // settled in Off then shows nothing.
  stepDownOnly?: boolean;
}): React.ReactElement {
  const { currentUser: user } = React.useContext(AuthContext);
  const canManageSiteZoneMode = user.isAllowed(to.manageSiteZonesMode);
  const [needsRefresh, setNeedsRefresh] = React.useState(false);
  const { data, loading, error, refetch, startPolling, stopPolling } =
    useGetSiteZonesModeQuery({
      variables: { siteId },
      skip: !canManageSiteZoneMode,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      onCompleted: () => setNeedsRefresh(false),
    });
  const [changeMode] = useChangeZonesModeMutation();
  const [confirmation, setConfirmation] = React.useState<{
    siteId: string;
    fromMode: ZonesMode;
    toMode: ZonesMode;
    latestChangeId: string | null;
  } | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [stepDownShown, setStepDownShown] = React.useState(false);
  const locked = React.useRef(false);
  const site = data?.getJobsite;
  const target = confirmation?.toMode ?? null;
  const copy = confirmation
    ? confirmationCopy(confirmation.fromMode, confirmation.toMode)
    : null;
  // A confirmation stays open only while the site shows the mode and latest
  // change the admin saw when opening it. Another admin can roll back and
  // re-enable without changing the mode, so the change id is compared too.
  const confirmationCurrent =
    confirmation?.siteId === siteId &&
    confirmation?.fromMode === site?.zonesMode &&
    confirmation?.latestChangeId === (site?.latestZonesModeChange?.id ?? null);
  React.useEffect(() => {
    if (confirmation && !confirmationCurrent) setConfirmation(null);
  }, [confirmation, confirmationCurrent]);
  const choose = (toMode: ZonesMode): void => {
    if (!site) return;
    setActionError(null);
    setConfirmation({
      siteId,
      fromMode: site.zonesMode,
      toMode,
      latestChangeId: site.latestZonesModeChange?.id ?? null,
    });
  };
  const running =
    site?.latestZonesModeChange?.status === ZonesModeChangeStatus.Running;
  // Separate field reads can straddle completion. Keep reading until they agree.
  const awaitingCompletedMode =
    site?.latestZonesModeChange?.status === ZonesModeChangeStatus.Succeeded &&
    site.zonesMode !== site.latestZonesModeChange.toMode;
  const blocked =
    loading ||
    Boolean(error) ||
    submitting ||
    needsRefresh ||
    running ||
    awaitingCompletedMode;
  // A completed change queues the site's access group synchronization, which
  // the dispatcher normally finishes within seconds. Keep reading until that
  // readiness item clears, so the next button does not stay disabled behind
  // a manual refresh; stop after thirty seconds, since a failing delivery
  // backs off for minutes.
  const syncPending =
    site?.zonesReadiness.items.some(
      (item) => item.code === ZonesReadinessCode.AccessGroupSync && !item.ready,
    ) ?? false;
  const syncPolls = React.useRef(0);
  React.useEffect(() => {
    syncPolls.current = syncPending ? syncPolls.current + 1 : 0;
  }, [data, syncPending]);
  const followingSync = syncPending && syncPolls.current < 15;

  React.useEffect(() => {
    if (
      canManageSiteZoneMode &&
      (running || needsRefresh || awaitingCompletedMode || followingSync)
    )
      startPolling(2000);
    else stopPolling();
    return stopPolling;
  }, [
    canManageSiteZoneMode,
    running,
    needsRefresh,
    awaitingCompletedMode,
    followingSync,
    startPolling,
    stopPolling,
  ]);
  // Stepping down only, a site settled in Off has nothing to show, and the
  // first read shows nothing until it settles. Once shown, the controls stay
  // so a rollback's outcome remains visible.
  const stepDownHidden =
    stepDownOnly &&
    !stepDownShown &&
    (site ? site.zonesMode === ZonesMode.Off && !running : !error);
  // Full control resets it, so stepping down only later starts hidden again.
  React.useEffect(() => {
    if (!stepDownOnly) setStepDownShown(false);
    else if (site && !stepDownHidden) setStepDownShown(true);
  }, [site, stepDownOnly, stepDownHidden]);

  const refresh = async (): Promise<void> => {
    const fresh = (await refetch()).data?.getJobsite;
    if (
      fresh?.latestZonesModeChange?.status === ZonesModeChangeStatus.Running ||
      (target && fresh?.zonesMode === target)
    )
      setConfirmation(null);
    setNeedsRefresh(false);
  };
  const submit = async (): Promise<void> => {
    if (
      !target ||
      !site ||
      !confirmationCurrent ||
      blocked ||
      locked.current ||
      target === site.zonesMode
    )
      return;
    if (requiresReadiness(site.zonesMode, target) && !site.zonesReadiness.ready)
      return;
    locked.current = true;
    setSubmitting(true);
    setActionError(null);
    try {
      await changeMode({
        variables: {
          input: {
            siteId,
            fromMode: site.zonesMode,
            toMode: target,
            latestChangeId: site.latestZonesModeChange?.id ?? null,
          },
        },
      });
      setConfirmation(null);
    } catch (failure: unknown) {
      setActionError(
        failure instanceof Error
          ? failure.message
          : 'The request could not be confirmed.',
      );
    } finally {
      // Even a network error can follow a committed request. Read before
      // allowing another change; `submitting` blocks changes during the read.
      try {
        await refresh();
      } catch {
        setNeedsRefresh(true);
        setActionError(
          'The latest mode change could not be loaded. Refresh before making another change.',
        );
        setConfirmation(null);
      }
      locked.current = false;
      setSubmitting(false);
    }
  };
  if (!canManageSiteZoneMode) return null;
  if (stepDownHidden) return null;
  const retrying = (toMode: ZonesMode): boolean =>
    site?.latestZonesModeChange?.status === ZonesModeChangeStatus.Failed &&
    site.latestZonesModeChange.toMode === toMode;
  const returning = site?.zonesMode === ZonesMode.Enforce;
  return (
    <Section
      id="zones-mode"
      title="Zones mode"
      details="Review access in Shadow, then apply group permissions in Enforce."
    >
      <div className="odin-col-span-6 odin-space-y-5 odin-text-sm">
        {loading && !site && <LoadingError loading />}
        {submitting && !target && (
          <p role="status">Confirming the latest zones state…</p>
        )}
        {awaitingCompletedMode && (
          <p role="status">Refreshing the completed mode change…</p>
        )}
        {(error || needsRefresh) && (
          <div className="odin-space-y-3">
            <p role="alert">
              The latest zones state could not be confirmed. Refresh to
              continue.
            </p>
            <Button
              text="Refresh zones state"
              theme="secondary"
              disabled={loading || submitting}
              onClick={() => {
                void refresh().catch((): void => undefined);
              }}
            />
          </div>
        )}
        {site && (
          <>
            <div className="odin-flex odin-items-center odin-gap-3">
              <span className="odin-font-semibold">Current mode</span>
              <Badge text={modeLabels[site.zonesMode]} color="gray" />
            </div>
            <p className="odin-text-gray-600">
              {modeDescriptions[site.zonesMode]}
            </p>
            <div className="odin-rounded-lg odin-border odin-border-gray-200 odin-p-4 odin-space-y-3">
              <div className="odin-flex odin-flex-wrap odin-items-center odin-justify-between odin-gap-3">
                <h3 className="odin-font-semibold">Readiness</h3>
                <Button
                  text="Refresh readiness"
                  theme="secondary"
                  disabled={loading || submitting}
                  onClick={() => {
                    void refresh().catch((): void => undefined);
                  }}
                />
              </div>
              <ul className="odin-space-y-2">
                {site.zonesReadiness.items.map((item) => (
                  <li key={item.code} className="odin-flex odin-gap-2">
                    <span
                      aria-hidden="true"
                      className={
                        item.ready
                          ? 'odin-text-green-700'
                          : 'odin-text-amber-700'
                      }
                    >
                      {item.ready ? '✓' : '!'}
                    </span>
                    <span>
                      <span className="odin-sr-only">
                        {item.ready ? 'Ready: ' : 'Needs attention: '}
                      </span>
                      {item.message}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            {site.latestZonesModeChange && (
              <ZonesModeProgress
                key={site.latestZonesModeChange.id}
                change={site.latestZonesModeChange}
                timeZone={site.timeZone ?? 'UTC'}
              />
            )}
            {!stepDownOnly && !running && site.zonesMode === ZonesMode.Off && (
              <Button
                text={
                  retrying(ZonesMode.Shadow)
                    ? 'Retry enabling Shadow'
                    : 'Enable Shadow'
                }
                disabled={blocked || !site.zonesReadiness.ready}
                onClick={() => {
                  choose(ZonesMode.Shadow);
                }}
              />
            )}
            {!running && site.zonesMode === ZonesMode.Shadow && (
              <div className="odin-flex odin-flex-wrap odin-gap-3">
                {!stepDownOnly && (
                  <Button
                    text={
                      retrying(ZonesMode.Enforce)
                        ? 'Retry enabling Enforce'
                        : 'Enable Enforce'
                    }
                    disabled={blocked || !site.zonesReadiness.ready}
                    onClick={() => choose(ZonesMode.Enforce)}
                  />
                )}
                <Button
                  text={
                    retrying(ZonesMode.Off)
                      ? 'Retry rollback to Off'
                      : 'Roll back to Off'
                  }
                  theme="secondary"
                  disabled={blocked}
                  onClick={() => {
                    choose(ZonesMode.Off);
                  }}
                />
              </div>
            )}
            {!running && returning && (
              <Button
                text={
                  retrying(ZonesMode.Shadow)
                    ? 'Retry returning to Shadow'
                    : 'Return to Shadow'
                }
                theme="secondary"
                disabled={blocked}
                onClick={() => choose(ZonesMode.Shadow)}
              />
            )}
          </>
        )}
        {!target && <ActionFeedback error={actionError} />}
        {target && confirmationCurrent && copy && (
          <Modal
            open
            setOpen={(open) => {
              if (!open && !submitting) setConfirmation(null);
            }}
            size="sm"
            title={copy.title}
            actionText={copy.actionText}
            onAction={submit}
            actionButtonEnabled={
              !blocked &&
              confirmationCurrent &&
              (!requiresReadiness(confirmation.fromMode, target) ||
                Boolean(site?.zonesReadiness.ready))
            }
            actionButtonWithSpinner={submitting}
            cancelButtonEnabled={!submitting}
          >
            <div className="odin-space-y-4">
              <p>{copy.description}</p>
              <ActionFeedback error={actionError} />
            </div>
          </Modal>
        )}
      </div>
    </Section>
  );
}
